// services/reportService.ts
import { apiService } from './api';

export interface DashboardStats {
  cash_in_hand:         number;
  today_cash_sales:     number;
  today_due_sales:      number;
  today_total_sales:    number;
  today_retail_cash:    number;
  today_retail_due:     number;
  today_wholesale_cash: number;
  today_wholesale_due:  number;
  today_purchase_amount:number;
  today_cost_amount:    number;
  today_total_entries:  number;
  today_pending:        number;
  today_verified:       number;
  petty_cash:           number;
}

export interface StockRow {
  sl: number; product_id: number; product_name: string; category_name: string | null;
  purchase: number; sell: number; return: number; stock: number;
  purchase_litre: number; sell_litre: number; return_litre: number; stock_litre: number; // ← NEW
  unit_of_measure: string; purchase_rate: number | null; purchase_total: number;
}

export interface PurchaseRow {
  purchase_info_id: number; purchase_date: string; product_name: string;
  category_name: string | null; quantity_pcs: number; purchase_rate: string;
  total_purchase_price: string; remarks: string | null; created_by: string;
  litre_per_unit?: string; total_litre?: string;
}

export interface SellingRow {
  selling_info_id: number; sale_date: string; seller_name: string | null;
  product_name: string; category_name: string | null;
  quantity_pcs: number; sell_rate: string; total_sales_price: string;
  cash_amount: string; due_amount: string; sell_type: string;
  purchase_rate: string; total_purchase_price: string; total_profit: string;
  business_cost_20pct: string; commission_40pct: string; profit_loss: string;
  remarks: string | null; created_by: string; litre_per_unit?: string; total_litre?: string;
}

export interface ReturnRow {
  return_info_id: number; return_date: string; seller_name: string | null;
  product_name: string; category_name: string | null;
  quantity_pcs: number; sell_rate: string; total_sales_price: string;
  cash_amount: string; due_amount: string; return_type: string;
  purchase_rate: string; total_purchase_price: string; total_profit: string;
  business_cost_20pct: string; commission_40pct: string; profit_loss: string;
  remarks: string | null; created_by: string; litre_per_unit?: string; total_litre?: string;
}

export interface PivotRow {
  selling_info_id: number; sale_date: string; seller_name: string | null;
  product_name: string; quantity_pcs: number;
  litre_per_unit?: string;     
  total_litre?: string;         
  total_sales_price: string; cash_amount: string; due_amount: string; total_profit: string;
  commission_40pct: string; profit_loss: string; sell_type: string;
}

export interface PivotGroup {
  rows:              PivotRow[];
  total_pcs:         number;
  total_litre:       number; 
  total_sales:       number;
  total_cash:        number;
  total_due:         number;
  total_profit:         number;
  total_commission:  number;
  total_profit_loss: number;
}

export interface DueGroup {
  rows:              PivotRow[];
  total_pcs:         number;
  total_sales:       number;
  total_cash:        number;
  total_due:         number;
  total_profit:         number;
  total_commission:  number;
  total_profit_loss: number;
}

export interface CostRow {
  cost_info_id: number; cost_date: string; cost_category_name: string | null;
  cost_name: string; cost_amount: string; remarks: string | null; created_by: string;
}

export interface RoiRow {
  roi_info_id:          number;
  invest_return_date:   string;
  investor_name:        string;
  roi_type_name:        string;
  invest_return_amount: string;
  remarks:              string | null;
  created_by:           string;
}

export interface AuditRow {
  module: string; record_id: string; record_date: string; reference_id: string | null;
  quantity: string | null; amount: string | null; remarks: string | null;
  created_by: string; created_at: string; updated_by: string | null; updated_at: string | null;
}

export interface ReportListResponse<T> {
  success: boolean; results: T[]; count: number;
  totals?: Record<string, number | null>;
}

export interface SoldProductRow {
  selling_info_id:   number;
  product_id:        number;
  product_name:      string;
  sell_rate:         number;
  quantity_pcs:      number;
  litre_per_unit:    number;   // ← NEW
  total_litre:       number;   // ← NEW
  total_sales_price: number;
  sell_type:         string;
  is_invoiced:       boolean;
  invoice_code:      string | null;
}
 
export interface InvoiceItemRow {
  invoice_item_id: number;
  selling_info:    number | null;
  product:         number | null;
  product_name:    string;
  sell_rate:       string;
  quantity_pcs:    number;
  total_litre:     number;   // ← NEW
  total_amount:    string;
}
 
export interface InvoiceRow {
  invoice_info_id: number;
  invoice_code:    string;
  invoice_date:    string;
  seller_info:     number | null;
  seller_name:     string | null;
  shop_name:       string | null;
  address:         string | null;
  cell:            string | null;
  total_quantity:  number;
  total_amount:    string;
  items?:          InvoiceItemRow[];
  remarks:         string | null;
  created_at:      string;
  created_by:      string;
}
 

class ReportService {
  private base = '';  // baseURL is already http://localhost:8000/api

  getDashboard() {
    return apiService.get<{ success: boolean; data: DashboardStats }>(`/trades/dashboard-stats/`);
  }

  // Stock
  getStockReport(params?: { product_id?: number }) {
    return apiService.get<ReportListResponse<StockRow>>(`/trades/stock-report/`, { params });
  }
  downloadStock(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/stock-report/`, `Stock_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }

  // Purchase
  getPurchaseReport(params?: Record<string, any>) {
    return apiService.get<ReportListResponse<PurchaseRow>>(`/trades/purchase-report/`, { params });
  }
  downloadPurchase(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/purchase-report/`, `Purchase_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }

  // Selling
  getSellingReport(params?: Record<string, any>) {
    return apiService.get<ReportListResponse<SellingRow>>(`/trades/selling-report/`, { params });
  }
  downloadSelling(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/selling-report/`, `Selling_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }

  // Return
  getReturnReport(params?: Record<string, any>) {
    return apiService.get<ReportListResponse<ReturnRow>>(`/trades/return-report/`, { params });
  }
  downloadReturn(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/return-report/`, `Return_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }

  // Pivot
  getPivotReport(params?: Record<string, any>) {
    return apiService.get<{ success: boolean; pivot: Record<string, PivotGroup>; grand_total: Record<string, number>; raw_count: number }>(`/trades/pivot-report/`, { params });
  }
  downloadPivot(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/pivot-report/`, `Pivot_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }

  // Due
  getDueReport(params?: Record<string, any>) {
    return apiService.get<{ success: boolean; due: Record<string, DueGroup>; grand_total: Record<string, number>; raw_count: number }>(`/trades/due-report/`, { params });
  }
  downloadDue(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/due-report/`, `Due_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }

  // Cost
  getCostReport(params?: Record<string, any>) {
    return apiService.get<ReportListResponse<CostRow>>(`/trades/cost-report/`, { params });
  }
  downloadCost(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/cost-report/`, `Cost_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }

  // ROI
  getRoiReport(params?: Record<string, any>) {
    return apiService.get<ReportListResponse<RoiRow>>(`/trades/roi-report/`, { params });
  }
  downloadRoi(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(
      `/trades/roi-report/`,
      `ROI_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`,
      { ...params, format: fmt },
    );
  }

  // Audit
  getAuditReport(params?: Record<string, any>) {
    return apiService.get<ReportListResponse<AuditRow>>(`/trades/audit-report/`, { params });
  }
  downloadAudit(fmt: 'excel' | 'pdf', params?: any) {
    return apiService.download(`/trades/audit-report/`, `Audit_Report.${fmt === 'excel' ? 'xlsx' : 'pdf'}`, { ...params, format: fmt });
  }
  // Sold products for a date + seller (Condition #1)
  getSoldProducts(params: { invoice_date: string; seller_info_id: number }) {
    return apiService.get<{ success: boolean; results: SoldProductRow[]; count: number }>(
      `/trades/invoice-sold-products/`, { params },
    );
  }
  
  // Existing invoices (history)
  getInvoices(params?: Record<string, any>) {
    return apiService.get<{ success: boolean; results: InvoiceRow[]; count: number }>(
      `/trades/invoices/`, { params },
    );
  }
  
  // Generate an invoice (Condition #2)
  createInvoice(body: {
    invoice_date: string;
    seller_info_id: number;
    selling_info_ids: number[];
    remarks?: string;
  }) {
    return apiService.post<{ success: boolean; message: string; data: InvoiceRow }>(
      `/trades/invoices/`, body,
    );
  }
  
  // Single invoice detail by code
  getInvoiceByCode(code: string) {
    return apiService.get<{ success: boolean; data: InvoiceRow }>(
      `/trades/invoices/${encodeURIComponent(code)}/`,
    );
  }
  
  // Download PDF by code (Conditions #2 & #5)
  downloadInvoice(code: string) {
    return apiService.download(
      `/trades/invoices/${encodeURIComponent(code)}/download/`,
      `Invoice_${code}.pdf`,
    );
  }
  
  // Delete an invoice (releases its sale lines for re-invoicing)
  deleteInvoice(code: string) {
    return apiService.delete<{ success: boolean; message: string }>(
      `/trades/invoices/${encodeURIComponent(code)}/`,
    );
  }
}

export const reportService = new ReportService();
